import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useGoToBaseUrl} from '../../Panel/hooks/hooks';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {type NamedWorkspaceMenuItem} from '../types';
import {
  NamedWorkspaceMenuItemComp,
  type NamedWorkspaceMenuItemProps,
} from './NamedWorkspaceMenuItem';
import {NamedWorkspacePinButton} from './NamedWorkspacePinButton';

type NamedWorkspaceMenuGroupProps = {
  options: NamedWorkspaceMenuItem[];
  defaultMaxItems: number;
  testId?: string;
  menuItemComponent?: React.ComponentType<NamedWorkspaceMenuItemProps>;
  showLegacyCurrentViewCheckmark?: boolean;
  showPinActions?: boolean;
};

export const NamedWorkspaceMenuGroup = ({
  options,
  defaultMaxItems,
  testId,
  menuItemComponent: MenuItemComponent = NamedWorkspaceMenuItemComp,
  showLegacyCurrentViewCheckmark = false,
  showPinActions = false,
}: NamedWorkspaceMenuGroupProps) => {
  const {currentWorkspaceViewRef, onMenuItemClick, viewer} =
    useViewBarContext();
  const [shouldShowAll, setShouldShowAll] = useState(false);

  const dropdownItems =
    !shouldShowAll && options.length > defaultMaxItems
      ? options.slice(0, defaultMaxItems)
      : options;
  const canShowPinActions = showPinActions && viewer != null;
  const pinnedDividerIndex = canShowPinActions
    ? options.findLastIndex(option => option.starred)
    : -1;
  const showPinnedDivider =
    pinnedDividerIndex >= 0 &&
    options.slice(pinnedDividerIndex + 1).some(option => !option.starred);
  const goToBaseURL = useGoToBaseUrl();

  return (
    <>
      <DropdownMenu.Group>
        {dropdownItems.map((option, index) => {
          return (
            <React.Fragment
              key={
                option.viewCid + option.namedWorkspaceId.id + option.optionType
              }>
              <div className="group relative">
                <DropdownMenu.CheckboxItem
                  id={option.namedWorkspaceId.id}
                  className={twMerge(
                    'items-start text-wb-text-primary dark:text-moon-300',
                    canShowPinActions && 'pr-40',
                    !showLegacyCurrentViewCheckmark &&
                      'radix-state-checked:bg-wb-surface-option-selected radix-state-checked:rounded-s'
                  )}
                  checked={currentWorkspaceViewRef.id === option.viewCid}
                  data-test={testId}
                  data-perf-action="workspace-switch"
                  onClick={() => {
                    onMenuItemClick(option.viewCid, option.namedWorkspaceId);
                    goToBaseURL();
                  }}>
                  <MenuItemComponent option={option} />
                  {showLegacyCurrentViewCheckmark && (
                    <DropdownMenu.ItemIndicator>
                      <Icon name="checkmark" width={18} height={18} />
                    </DropdownMenu.ItemIndicator>
                  )}
                </DropdownMenu.CheckboxItem>
                {canShowPinActions && (
                  <div className="absolute right-10 top-1/2 -translate-y-1/2">
                    <NamedWorkspacePinButton option={option} />
                  </div>
                )}
              </div>
              {showPinnedDivider && index === pinnedDividerIndex && (
                <DropdownMenu.Separator className="ml-[3rem] mr-10" />
              )}
            </React.Fragment>
          );
        })}
      </DropdownMenu.Group>
      {options.length > defaultMaxItems && (
        <p
          className={twMerge(
            'my-6 ml-[3rem] cursor-pointer text-base font-semibold leading-[24px]',
            'text-wb-text-link hover:text-wb-text-link-hover'
          )}
          onClick={() => setShouldShowAll(prev => !prev)}>
          {shouldShowAll ? 'Show less' : 'Show all'}
        </p>
      )}
    </>
  );
};
